import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  DatePicker,
  InputNumber,
  Space,
  Tag,
  Typography,
  Alert,
  message,
  Popconfirm,
  Row,
  Col,
  Statistic,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DollarOutlined,
  PercentageOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { supabase } from "../../lib/supabase";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;

interface Company {
  id: number;
  name: string;
  status: string;
}

interface PricingComponent {
  id: number;
  name: string;
  description: string;
  calculation_type: string;
}

interface CompanyPricingOverride {
  id?: number;
  company_id: number;
  pricing_component_id: number;
  override_type: "fixed_price" | "percentage_adjustment" | "disabled";
  fixed_value?: number;
  fixed_currency?: string;
  fixed_included_products?: number;
  fixed_extra_product_cost?: number;
  percentage_adjustment?: number;
  notes?: string;
  is_active: boolean;
  valid_from?: string;
  valid_to?: string;
  created_at?: string;
  updated_at?: string;
  // Joined data
  company_name?: string;
  component_name?: string;
  component_description?: string;
}

interface CompanyPricingOverrideFormValues {
  company_id: number;
  pricing_component_id: number;
  override_type: "fixed_price" | "percentage_adjustment" | "disabled";
  fixed_value?: number;
  fixed_currency?: string;
  fixed_included_products?: number;
  fixed_extra_product_cost?: number;
  percentage_adjustment?: number;
  notes?: string;
  is_active: boolean;
  valid_from?: dayjs.Dayjs;
  valid_to?: dayjs.Dayjs;
}

const CompanyPricingOverrides: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [components, setComponents] = useState<PricingComponent[]>([]);
  const [overrides, setOverrides] = useState<CompanyPricingOverride[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingOverride, setEditingOverride] =
    useState<CompanyPricingOverride | null>(null);
  const [form] = Form.useForm();

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load companies
      const { data: companiesData } = await supabase
        .from("company")
        .select("id, name, status")
        .order("name");

      // Load pricing components
      const { data: componentsData } = await supabase
        .from("pricing_components")
        .select("id, name, description, calculation_type")
        .order("name");

      // Load existing overrides with joined data
      const { data: overridesData } = await supabase
        .from("company_pricing_overrides")
        .select(
          `
          *,
          company:company_id(name),
          component:pricing_component_id(name, description)
        `
        )
        .order("created_at", { ascending: false });

      setCompanies(companiesData || []);
      setComponents(componentsData || []);
      setOverrides(overridesData || []);
    } catch (error) {
      console.error("Error loading data:", error);
      message.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingOverride(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (override: CompanyPricingOverride) => {
    setEditingOverride(override);
    form.setFieldsValue({
      ...override,
      valid_from: override.valid_from ? dayjs(override.valid_from) : undefined,
      valid_to: override.valid_to ? dayjs(override.valid_to) : undefined,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const { error } = await supabase
        .from("company_pricing_overrides")
        .delete()
        .eq("id", id);

      if (error) throw error;

      message.success("Override deleted successfully");
      loadData();
    } catch (error) {
      console.error("Error deleting override:", error);
      message.error("Failed to delete override");
    }
  };

  const handleSubmit = async (values: CompanyPricingOverrideFormValues) => {
    try {
      const overrideData = {
        ...values,
        valid_from: values.valid_from?.format("YYYY-MM-DD") || null,
        valid_to: values.valid_to?.format("YYYY-MM-DD") || null,
      };

      if (editingOverride?.id) {
        // Update existing
        const { error } = await supabase
          .from("company_pricing_overrides")
          .update(overrideData)
          .eq("id", editingOverride.id);

        if (error) throw error;
        message.success("Override updated successfully");
      } else {
        // Create new
        const { error } = await supabase
          .from("company_pricing_overrides")
          .insert(overrideData);

        if (error) throw error;
        message.success("Override created successfully");
      }

      setModalVisible(false);
      loadData();
    } catch (error) {
      console.error("Error saving override:", error);
      message.error("Failed to save override");
    }
  };

  const getOverrideTypeIcon = (type: string) => {
    switch (type) {
      case "fixed_price":
        return <DollarOutlined style={{ color: "#52c41a" }} />;
      case "percentage_adjustment":
        return <PercentageOutlined style={{ color: "#1890ff" }} />;
      case "disabled":
        return <StopOutlined style={{ color: "#ff4d4f" }} />;
      default:
        return null;
    }
  };

  const getOverrideTypeLabel = (type: string) => {
    switch (type) {
      case "fixed_price":
        return "Fixed Price";
      case "percentage_adjustment":
        return "Percentage Adjustment";
      case "disabled":
        return "Disabled";
      default:
        return type;
    }
  };

  const getOverrideValue = (override: CompanyPricingOverride) => {
    switch (override.override_type) {
      case "fixed_price":
        return `${override.fixed_currency || "EUR"} ${override.fixed_value}`;
      case "percentage_adjustment":
        return `${override.percentage_adjustment}%`;
      case "disabled":
        return "Disabled";
      default:
        return "-";
    }
  };

  const columns = [
    {
      title: "Company",
      dataIndex: "company_name",
      key: "company_name",
      render: (name: string, record: CompanyPricingOverride) => (
        <div>
          <Text strong>{name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: "12px" }}>
            ID: {record.company_id}
          </Text>
        </div>
      ),
    },
    {
      title: "Component",
      dataIndex: "component_name",
      key: "component_name",
      render: (name: string, record: CompanyPricingOverride) => (
        <div>
          <Text strong>{name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {record.component_description}
          </Text>
        </div>
      ),
    },
    {
      title: "Override Type",
      dataIndex: "override_type",
      key: "override_type",
      render: (type: string) => (
        <Space>
          {getOverrideTypeIcon(type)}
          <Text>{getOverrideTypeLabel(type)}</Text>
        </Space>
      ),
    },
    {
      title: "Value",
      key: "value",
      render: (record: CompanyPricingOverride) => (
        <Text strong>{getOverrideValue(record)}</Text>
      ),
    },
    {
      title: "Validity",
      key: "validity",
      render: (record: CompanyPricingOverride) => (
        <div>
          <div>From: {record.valid_from || "Always"}</div>
          <div>To: {record.valid_to || "Always"}</div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      render: (active: boolean) => (
        <Tag color={active ? "green" : "red"}>
          {active ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (record: CompanyPricingOverride) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this override?"
            onConfirm={() => handleDelete(record.id!)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={3}>Company Pricing Overrides</Title>
        <Text type="secondary">
          Manage custom pricing overrides for specific companies. These
          overrides will be applied when generating invoices for the selected
          companies.
        </Text>
      </div>

      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Overrides"
              value={overrides.length}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Overrides"
              value={overrides.filter((o) => o.is_active).length}
              prefix={<DollarOutlined />}
              valueStyle={{ color: "#3f8600" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Companies with Overrides"
              value={new Set(overrides.map((o) => o.company_id)).size}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Components with Overrides"
              value={new Set(overrides.map((o) => o.pricing_component_id)).size}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Actions */}
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          Add Company Override
        </Button>
      </div>

      {/* Overrides Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={overrides}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} overrides`,
          }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={
          editingOverride ? "Edit Company Override" : "Add Company Override"
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            override_type: "fixed_price",
            fixed_currency: "EUR",
            is_active: true,
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="company_id"
                label="Company"
                rules={[{ required: true, message: "Please select a company" }]}
              >
                <Select placeholder="Select company" showSearch>
                  {companies.map((company) => (
                    <Option key={company.id} value={company.id}>
                      {company.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="pricing_component_id"
                label="Pricing Component"
                rules={[
                  { required: true, message: "Please select a component" },
                ]}
              >
                <Select placeholder="Select component" showSearch>
                  {components.map((component) => (
                    <Option key={component.id} value={component.id}>
                      {component.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="override_type"
                label="Override Type"
                rules={[
                  { required: true, message: "Please select override type" },
                ]}
              >
                <Select>
                  <Option value="fixed_price">
                    <Space>
                      <DollarOutlined style={{ color: "#52c41a" }} />
                      Fixed Price
                    </Space>
                  </Option>
                  <Option value="percentage_adjustment">
                    <Space>
                      <PercentageOutlined style={{ color: "#1890ff" }} />
                      Percentage Adjustment
                    </Space>
                  </Option>
                  <Option value="disabled">
                    <Space>
                      <StopOutlined style={{ color: "#ff4d4f" }} />
                      Disabled
                    </Space>
                  </Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="is_active"
                label="Active"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const overrideType = getFieldValue("override_type");

              return (
                <>
                  {overrideType === "fixed_price" && (
                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item
                          name="fixed_value"
                          label="Fixed Value"
                          rules={[
                            {
                              required: true,
                              message: "Please enter fixed value",
                            },
                          ]}
                        >
                          <InputNumber
                            placeholder="0.00"
                            min={0}
                            step={0.01}
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="fixed_currency" label="Currency">
                          <Select>
                            <Option value="EUR">EUR</Option>
                            <Option value="USD">USD</Option>
                            <Option value="CNY">CNY</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          name="fixed_included_products"
                          label="Included Products (for tiered pricing)"
                        >
                          <InputNumber
                            placeholder="1"
                            min={1}
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}

                  {overrideType === "percentage_adjustment" && (
                    <Form.Item
                      name="percentage_adjustment"
                      label="Percentage Adjustment"
                      rules={[
                        { required: true, message: "Please enter percentage" },
                      ]}
                    >
                      <InputNumber
                        placeholder="100"
                        min={0}
                        max={1000}
                        step={0.1}
                        addonAfter="%"
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  )}

                  {overrideType === "disabled" && (
                    <Alert
                      message="Component will be disabled"
                      description="This component will not be included in invoice calculations for this company."
                      type="warning"
                      showIcon
                    />
                  )}
                </>
              );
            }}
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="valid_from" label="Valid From">
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="Start date (optional)"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="valid_to" label="Valid To">
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="End date (optional)"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="Notes">
            <Input.TextArea
              rows={3}
              placeholder="Optional notes about this override"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingOverride ? "Update Override" : "Create Override"}
              </Button>
              <Button onClick={() => setModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CompanyPricingOverrides;
